import {
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  useMemo,
  useState,
} from "react";
import { Toaster, toast } from "sonner";

const HTTP_URL="https://pdf-chat-be.amitesh.work";

type Message = {
  text: string;
  id: string;
  role: "user" | "ai";
};

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function App() {
  const [chatting, setChatting] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [query, setQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isAsking, setIsAsking] = useState(false);

  const canSend = useMemo(
    () => chatting && query.trim().length >= 3 && !isAsking,
    [chatting, query, isAsking],
  );

  async function upload(file: File | undefined) {
    if (!file) {
      toast.error("Please choose a PDF file first");
      return;
    }

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are supported");
      return;
    }

    setSelectedFile(file);
    setIsUploading(true);
    const toastId = toast.loading("Uploading and indexing your PDF...");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${HTTP_URL}/upload-pdf`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      setChatting(true);
      setMessages([]);
      toast.success("PDF is ready. You can start asking questions now.", {
        id: toastId,
      });
    } catch {
      toast.error("Something went wrong while uploading the PDF", {
        id: toastId,
      });
    } finally {
      setIsUploading(false);
    }
  }

  async function ask() {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 3) {
      toast.error("Please enter at least 3 characters");
      return;
    }

    setIsAsking(true);
    const userMessage: Message = {
      id: crypto.randomUUID(),
      text: trimmedQuery,
      role: "user",
    };

    setMessages((prev) => [...prev, userMessage]);
    setQuery("");

    const toastId = toast.loading("Thinking...");

    try {
      const response = await fetch(`${HTTP_URL}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: trimmedQuery }),
      });
      
      if (response.status === 429) {
        toast.error("Too many requests, try again after few minutes.", {
          id: toastId,
        });
        setMessages((prev) =>
          prev.filter((message) => message.id !== userMessage.id),
        );
        setQuery(trimmedQuery);
        return;
      }

      if (!response.ok) {
        throw new Error("Chat request failed");
      }
      
      const data = await response.json();
      const { message } = data;

      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          text: message?.trim() || "Answer not available",
          role: "ai",
        },
      ]);

      toast.success("Response received", { id: toastId });
    } catch  {
      toast.error("Something went wrong while fetching the answer", {
        id: toastId,
      });
      setMessages((prev) =>
        prev.filter((message) => message.id !== userMessage.id),
      );
      setQuery(trimmedQuery);
    } finally {
      setIsAsking(false);
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    upload(event.target.files?.[0]);
  }

  function handleDragOver(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(false);
    upload(event.dataTransfer.files?.[0]);
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" && canSend) {
      ask();
    }
  }

  return (
    <main className="min-h-screen bg-stone-950 px-4 py-8 text-stone-100 sm:px-6 lg:px-8">
      <Toaster richColors position="top-right" theme="dark" />

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 lg:flex-row">
        <section className="w-full rounded-3xl border border-stone-800 bg-stone-900 p-6 shadow-2xl shadow-black/20 lg:max-w-sm">
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">
              PDF Chat
            </p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-stone-100">
              Ask your document simple questions
            </h1>
            <p className="mt-3 text-sm leading-6 text-stone-400">
              Upload a PDF, wait for indexing, then ask questions in a clean
              chat view.
            </p>
          </div>

          <label
            className={`group flex min-h-64 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-8 text-center transition ${
              isDragging
                ? "border-stone-500 bg-stone-800"
                : "border-stone-700 bg-stone-900/80 hover:border-stone-500 hover:bg-stone-800"
            } ${isUploading ? "pointer-events-none opacity-70" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFileChange}
              disabled={isUploading}
            />

            <div className="rounded-full border border-stone-700 bg-stone-950 px-4 py-2 text-xs font-medium text-stone-300 shadow-sm transition group-hover:border-stone-500">
              {isUploading ? "Uploading..." : "Choose PDF"}
            </div>

            <p className="mt-5 text-base font-medium text-stone-200">
              Drag and drop your PDF here
            </p>
            <p className="mt-2 text-sm text-stone-500">
              or click to browse from your device
            </p>

            {selectedFile ? (
              <div className="mt-6 w-full rounded-2xl border border-stone-700 bg-stone-950 px-4 py-3 text-left">
                <p className="truncate text-sm font-medium text-stone-200">
                  {selectedFile.name}
                </p>
                <p className="mt-1 text-xs text-stone-500">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
            ) : null}
          </label>

          <div className="mt-5 rounded-2xl border border-stone-800 bg-stone-950 px-4 py-3 text-sm text-stone-400">
            <p>
              Status: {chatting ? "Ready for chat" : "Waiting for PDF upload"}
            </p>
          </div>
        </section>

        <section className="flex min-h-[38rem] w-full flex-col rounded-3xl border border-stone-800 bg-stone-900 shadow-2xl shadow-black/20">
          <div className="border-b border-stone-800 px-6 py-5">
            <h2 className="text-lg font-semibold text-stone-100">
              Conversation
            </h2>
            <p className="mt-1 text-sm text-stone-500">
              {chatting
                ? "Ask anything based on the uploaded PDF."
                : "Upload a PDF to start the conversation."}
            </p>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-6">
            {messages.length ? (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`max-w-2xl rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm ${
                    message.role === "user"
                      ? "ml-auto bg-stone-100 text-stone-900"
                      : "border border-stone-800 bg-stone-950 text-stone-200"
                  }`}
                >
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.22em] opacity-60">
                    {message.role === "user" ? "You" : "Assistant"}
                  </p>
                  <p className="whitespace-pre-wrap break-words">
                    {message.text}
                  </p>
                </div>
              ))
            ) : (
              <div className="flex h-full min-h-80 items-center justify-center rounded-2xl border border-dashed border-stone-800 bg-stone-950 px-6 text-center">
                <div>
                  <p className="text-base font-medium text-stone-300">
                    No messages yet
                  </p>
                  <p className="mt-2 text-sm text-stone-500">
                    Upload a PDF and ask a question to see the conversation
                    here.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-stone-800 px-6 py-5">
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={
                  chatting
                    ? "Ask a question from the PDF..."
                    : "Upload a PDF before asking a question"
                }
                disabled={!chatting || isAsking}
                className="py-4 flex-1 rounded-2xl border border-stone-700 bg-stone-950 px-4 text-sm text-stone-100 outline-none transition placeholder:text-stone-500 focus:border-stone-500 focus:bg-stone-900 disabled:cursor-not-allowed disabled:opacity-60"
              />

              <button
                type="button"
                onClick={ask}
                disabled={!canSend}
                className="h-12 rounded-2xl bg-stone-100 px-6 text-sm font-medium text-stone-900 transition hover:bg-stone-300 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-400"
              >
                {isAsking ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

export default App;
